/**
 * Shared read-only table column definitions for the ExAnte confirmation steps.
 *
 * Uses standard DashboardTable structure (withInputs=false).
 * Reuses logic from TradeStepV2 columns where possible.
 */
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import {InstrumentNameCell, SRRIColumn} from '../../../../../../Modelportfolios/components/InstrumentsList/table-data';
import {SustainabilityCell} from '../../../../../../../utils/commonTableColumns';
import {
  TRADING_ACTION_SELL, TRADING_ACTIONS
} from '../../../../../../../components/Charts/InstrumentsAllocationTable/constants';
import {
  getModelPortfolioTransactionTypeName,
  SAVING_PLAN_TRANSACTION_TYPE,
  TRANSACTION_INSTR
} from '../../../../../constants';
import {getAmount, getBookingDate, getTransactionItemSRRI} from '../../../../../utils';
import {getItemBrokerFeeData, PortfolioNameColumn} from '../../../../TradeStep/components/PortfolioTrade/table-data';
import {withEuroOrDash, formatQty, withPercentOrDash} from '../../../../../../../utils/utils';
import WarningTooltip from "../../../../../../../components/WarningTooltip";
import {DATE_FORMAT} from "../../../../../../../utils/constants";
import {SwitchInIcon} from "../../../../TradeStepV2/components/shared/switchTableColumns";
import {PriceColumn} from "../../../../TradeStepV2/components/shared/modelPortfolioTableColumns";
import {
  LimitOrderColumnNew, NumberOfSharesColumnNew,
  ShareSettingsColumn
} from "../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/Portfolio/table-data";
import {
  ShareSettingsSummaryBody
} from "../../../../../../RiskProfiling/components/StepContent/components/step/ProductSelectionStepsV2/components/SummaryStep/table-data";
import {toGermanFormat} from "../../../../../../../utils/numberFormater";
import {AmountCol} from "../../../../TradeStepV2/components/PortfolioTradeV2/einzeltitelTable";
import {getTotal} from "../utils";

// Pre-build lookup map for O(1) access
const TRADING_ACTIONS_MAP = new Map(TRADING_ACTIONS.map(a => [a.value, a.text]));


// ─── Shared Columns ───────────────────────────────────────────────────────────

export const OrderNumberCol = {
  content: [{
    header: 'Nr.',
    body: { content: (item) => item.isSwitchInItem ? <SwitchInIcon /> : `${item._orderNumber}.` },
  }],
  align: 'center',
  key: 'orderNumber',
};

export const ProductNameCol = {
  content: [{
    header: InstrumentNameCell.header,
    body: { content: (item, options) => InstrumentNameCell.body.content(item.data, options) || null},
  }],
  key: 'productName',
  cellClassName: 'name-cell',
};

const MpNameCol = {
  content: [{
    header: {content: () => <>Produktname<br/>MP Nummer</>},
    body: {content: (item, options) => PortfolioNameColumn.body[0]({item, options})}
  }],
  key: 'productName',
  cellClassName: 'name-cell',
};

export const SustainabilityCol = {
  content: [{
    header: SustainabilityCell.header,
    body: {content: (item, options) => SustainabilityCell.body.content(item.data, options)}
  }],
  key: 'sustainability',
  cellClassName: 'sustainability-cell'
};

export const SRIReadOnlyCol = {
  content: [{
    header: SRRIColumn.header,
    body: {
      content: (item, options) => {
        const itemSRRI = getTransactionItemSRRI(item, options.isModelportfolio, true);
        return SRRIColumn.body.content({ internal_sri: itemSRRI }, options);
      },
    },
    footer: { content: () => <b>Gesamt</b> },
  }],
  key: 'sri',
};

const getFeeAndDiscount = (item, options) => getItemBrokerFeeData(item, { ...options, switchOutItem: item.switchOutItem })

export const AgioCol = {
  content: [{
    header: { content: () => <>Agio<WarningTooltip title="Agio = Ausgabeaufschlag" /><br/>Anmerkung</> },
    body: {
      content: (item, options) => {
        if (item.tradingType === TRADING_ACTION_SELL) return null;
        const {brokerFee, netFeeEur, discount} = getFeeAndDiscount(item, options);
        return (
          <span style={{whiteSpace: 'nowrap'}}>
            {withPercentOrDash(brokerFee, false)}
            <br/>
            {withEuroOrDash(netFeeEur)}
            <br/>
            {withPercentOrDash(discount)}
          </span>
        );
      },
    },
    footer: {
      content: ({items, options}) => {
        const total = items
          .filter(i => i.tradingType !== TRADING_ACTION_SELL)
          .reduce((sum, item) => sum + getFeeAndDiscount(item, options).netFeeEur, 0);
        return <b>{withEuroOrDash(total)}</b>;
      },
    },
  }],
  align: 'right',
  key: 'agio',
};

export const OrderTypeCol = {
  content: [{
    header: 'Auftragsart',
    body: { content: (item) => TRADING_ACTIONS_MAP.get(item.tradingType) },
  }],
  key: 'orderType',
  align: 'right',
};


// ─── Single Investment Specific ───────────────────────────────────────────────

export const SingleInvestmentAmountCol = {
  content: [{
    header: { content: () => <span>Menge/<br/>Betrag<br/>Ausführung</span> },
    body: {
      content: (item, options) => {
        const isQty = item.transaction_type === TRANSACTION_INSTR.qty;
        const amount = getAmount(item, null, item.switchOutItem);
        return (
          <>
            {isQty && <div>{formatQty(item.transaction_value, options.maxQtyDecimals)} Stck.</div>}
            <div>{withEuroOrDash(amount)}</div>
            <div>{getBookingDate(item, DATE_FORMAT) || 'Sofort'}</div>
          </>
        );
      },
    },
    footer: {
      content: ({items}) => <b>{withEuroOrDash(getTotal(items))}</b>,
    },
  }],
  align: 'right',
  key: 'amount',
};


// ─── Payment Plans Specific ───────────────────────────────────────────────────

export const PaymentPlanAmountCol = {
  content: [{
    header: { content: () => <span>Betrag<br/>Ausführung</span> },
    body: {
      content: (item, options) => {
        const amount = getAmount(item, null, item.switchOutItem);
        const rotationLabel = item.rotationLabel || _.get(item, 'switchOutItem.rotationLabel');
        const fromDate = item.from_date || _.get(item, 'switchOutItem.from_date');
        const tillDate = item.till_date || _.get(item, 'switchOutItem.till_date');
        return (
          <>
            <div>{withEuroOrDash(amount)}</div>
            <div>{rotationLabel}</div>
            <div>ab {moment(fromDate).format(DATE_FORMAT)}</div>
            {tillDate && <div>bis {moment(tillDate).format(DATE_FORMAT)}</div>}
          </>
        );
      },
    },
    footer: SingleInvestmentAmountCol.content[0].footer
  }],
  align: 'right',
  key: 'planAmount',
};

export const ContractOrderTypeCol = {
  content: [{
    header: { content: () => <span>Vertragsart<br/>Auftragsart</span> },
    body: {
      content: (item) => {
        const contractType = TRADING_ACTIONS_MAP.get(item.tradingType);
        const actionText = SAVING_PLAN_TRANSACTION_TYPE[item.action || _.get(item, 'switchOutItem.action')];
        return (
          <>
            <div>{contractType}</div>
            <div>{actionText}</div>
          </>
        );
      },
    },
  }],
  align: 'right',
  key: 'contractOrderType',
};


// ─── Table Structures ─────────────────────────────────────────────────────────

export const singleInvestmentConfirmationTableStructure = {
  default: [
    OrderNumberCol,
    ProductNameCol,
    SustainabilityCol,
    SRIReadOnlyCol,
    SingleInvestmentAmountCol,
    AgioCol,
    OrderTypeCol,
  ],
};

export const singleInvestmentConfirmationTableStructureEinzeltitel = {
  default: [
    OrderNumberCol,
    ProductNameCol,
    {
      content: [{
        header: {content: PriceColumn.header[0]},
        body: {content: (item) => PriceColumn.body[0]({item})}
      }],
      align: PriceColumn.align,
    },
    {
      content: [{
        header: {content: ShareSettingsColumn.header[0]},
        body: {content: (item) => <ShareSettingsSummaryBody item={item} />}
      }],
      cellClassName: 'einzeltitel-settings-cell'
    },
    {
      content: [{
        header: {content: LimitOrderColumnNew.header[0]},
        body: {content: (item) => withEuroOrDash(item.limit)}
      }],
      align: LimitOrderColumnNew.align,
    },
    {
      content: [{
        header: {content: NumberOfSharesColumnNew.header[0]},
        body: {content: (item) => toGermanFormat(item.quantity)},
        footer: {content: NumberOfSharesColumnNew.footer[0]}
      }],
      align: NumberOfSharesColumnNew.align,
    },
    {
      content: [{
        header: {content: AmountCol.header[0]},
        body: {content: (item) => withEuroOrDash(item.transaction_value)},
        footer: {content: AmountCol.footer[0]}
      }],
      align: AmountCol.align,
    },
    OrderTypeCol,
  ]
}

export const paymentPlansConfirmationTableStructure = {
  default: [
    OrderNumberCol,
    ProductNameCol,
    SustainabilityCol,
    SRIReadOnlyCol,
    PaymentPlanAmountCol,
    AgioCol,
    ContractOrderTypeCol,
  ],
};

const MPTradeType = {
  header: { content: () => 'Anlagevariante' },
  body: {
    content: (item, options) => {
      const action = React.useMemo(() => getModelPortfolioTransactionTypeName(
        TRADING_ACTIONS.find(a => a.value == item.tradingType).code,
        options.modelPortfolioTransactionType
      ), [options.modelPortfolioTransactionType]);

      return action;
    }
  }
}

const MPAgioCol = {
  ...AgioCol,
  content: [{
    ...AgioCol.content[0],
    header: {content: () => <>EE<WarningTooltip title="EE = Einstiegsentgelt"/><br/>Anmerkung</>}
  }]
}

export const singleInvestmentConfirmationTableStructureForMP = {
  default: [
    OrderNumberCol,
    MpNameCol,
    SRIReadOnlyCol,
    {
      ...SingleInvestmentAmountCol,
      content: [
        {
          ...SingleInvestmentAmountCol.content[0],
          header: {content: () => <span>Betrag<br/>Ausführung</span>}
        }
      ]
    },
    MPAgioCol,
    {
      content: [
        OrderTypeCol.content[0],
        MPTradeType
      ],
      align: 'right',
    }
  ],
};

export const paymentPlansConfirmationTableStructureForMP = {
  default: [
    OrderNumberCol,
    MpNameCol,
    SRIReadOnlyCol,
    PaymentPlanAmountCol,
    MPAgioCol,
    {
      content: [
        ContractOrderTypeCol.content[0],
        MPTradeType
      ],
      align: 'right',
    }
  ],
}
